/**
 * Hybrid persistence layer.
 *
 * Routes save/load/list/delete operations to the cloud backend when the user
 * is authenticated, and falls back to IndexedDB for anonymous or offline use.
 */

import type { MindMapDocument } from '@/model/document';
import { useAuthStore } from '@/store/authStore';
import { db } from '@/persistence/indexedDb';
import {
  createNewMap as idbCreateNewMap,
  openMap as idbOpenMap,
  getMapList as idbGetMapList,
  type MapListEntry,
} from '@/persistence/mapList';
import {
  cloudSaveMap,
  cloudLoadMap,
  cloudCreateMap,
  cloudListMaps,
  cloudDeleteMap,
} from '@/persistence/cloudPersistence';

function isAuthenticated(): boolean {
  return useAuthStore.getState().isAuthenticated;
}

/**
 * Save a map. Routes to cloud or IndexedDB based on auth state.
 */
export async function saveMap(
  mapDoc: MindMapDocument,
  mapId: string,
  title: string,
): Promise<void> {
  if (isAuthenticated()) {
    await cloudSaveMap(mapDoc, mapId, title);
  } else {
    const state = mapDoc.encodeState();
    await db.saveMap(mapId, title, state);
  }
}

/**
 * Load a map. Routes to cloud or IndexedDB based on auth state.
 */
export async function loadMap(
  mapId: string,
  mapDoc: MindMapDocument,
): Promise<void> {
  if (isAuthenticated()) {
    await cloudLoadMap(mapId, mapDoc);
  } else {
    await idbOpenMap(mapId, mapDoc);
  }
}

/**
 * Create a new map. Routes to cloud or IndexedDB based on auth state.
 * Returns the new map ID.
 */
export async function createMap(title: string): Promise<string> {
  if (isAuthenticated()) {
    const result = await cloudCreateMap(title);
    return result.mapId;
  } else {
    return idbCreateNewMap(title);
  }
}

/**
 * List all maps. Routes to cloud or IndexedDB based on auth state.
 * Returns a unified MapListEntry array.
 */
export async function listMaps(): Promise<MapListEntry[]> {
  if (isAuthenticated()) {
    const items = await cloudListMaps();
    return items.map((item) => ({
      id: item.mapId,
      title: item.title,
      updatedAt: new Date(item.updatedAt).getTime(),
      ownerEmail: item.ownerEmail,
      role: item.role,
    }));
  } else {
    return idbGetMapList();
  }
}

/**
 * Delete a map. Routes to cloud or IndexedDB based on auth state.
 */
export async function deleteMap(mapId: string): Promise<void> {
  if (isAuthenticated()) {
    await cloudDeleteMap(mapId);
  } else {
    await db.deleteMap(mapId);
  }
}
